/**
 * Forward Kinematics for Universal Robots
 *
 * Computes the end-effector pose (position and orientation) given joint angles
 * Uses Modified DH Convention
 */

/**
 * Create a 4x4 transformation matrix from DH parameters
 * @param {number} theta - Joint angle (radians)
 * @param {number} d - Link offset along previous z
 * @param {number} a - Link length along x
 * @param {number} alpha - Link twist about x
 * @returns {Array<Array<number>>} 4x4 transformation matrix
 */
function dhTransform(theta, d, a, alpha) {
  const ct = Math.cos(theta);
  const st = Math.sin(theta);
  const ca = Math.cos(alpha);
  const sa = Math.sin(alpha);

  return [
    [ct, -st * ca, st * sa, a * ct],
    [st, ct * ca, -ct * sa, a * st],
    [0, sa, ca, d],
    [0, 0, 0, 1],
  ];
}

/**
 * Multiply two 4x4 matrices
 * @param {Array<Array<number>>} a - First matrix
 * @param {Array<Array<number>>} b - Second matrix
 * @returns {Array<Array<number>>} Result matrix
 */
function multiplyMatrices(a, b) {
  const result = Array(4).fill(0).map(() => Array(4).fill(0));

  for (let i = 0; i < 4; i++) {
    for (let j = 0; j < 4; j++) {
      for (let k = 0; k < 4; k++) {
        result[i][j] += a[i][k] * b[k][j];
      }
    }
  }

  return result;
}

/**
 * Compute forward kinematics for UR robot
 * @param {Array<number>} joints - Array of 6 joint angles in radians [q1, q2, q3, q4, q5, q6]
 * @param {Object} dh - DH parameters for the robot
 * @param {Object} tcpOffset - Optional TCP offset {x, y, z, rx, ry, rz}
 * @returns {Object} End-effector pose { position: {x, y, z}, rotation: {rx, ry, rz} }
 */
export function forwardKinematics(joints, dh, tcpOffset = null) {
  if (joints.length !== 6) {
    throw new Error('Expected 6 joint angles');
  }

  const { d1, a2, a3, d4, d5, d6 } = dh;
  const [q1, q2, q3, q4, q5, q6] = joints;

  // Create transformation matrices for each joint using Modified DH Convention
  // Modified DH: T = Rot_Z(theta) * Trans_Z(d) * Trans_X(a) * Rot_X(alpha)
  const T01 = dhTransform(q1, d1, 0, Math.PI / 2);
  const T12 = dhTransform(q2, 0, a2, 0);
  const T23 = dhTransform(q3, 0, a3, 0);
  const T34 = dhTransform(q4, d4, 0, Math.PI / 2);
  const T45 = dhTransform(q5, d5, 0, -Math.PI / 2);
  const T56 = dhTransform(q6, d6, 0, 0);

  // Multiply all transformations to get flange pose
  let T06 = T01;
  T06 = multiplyMatrices(T06, T12);
  T06 = multiplyMatrices(T06, T23);
  T06 = multiplyMatrices(T06, T34);
  T06 = multiplyMatrices(T06, T45);
  T06 = multiplyMatrices(T06, T56);

  // Apply TCP offset if provided
  if (tcpOffset) {
    // Convert TCP rotation from axis-angle to rotation matrix
    const R_tcp = axisAngleToRotationMatrix(tcpOffset.rx, tcpOffset.ry, tcpOffset.rz);

    // Create TCP transformation matrix
    const T_tcp = [
      [R_tcp[0][0], R_tcp[0][1], R_tcp[0][2], tcpOffset.x],
      [R_tcp[1][0], R_tcp[1][1], R_tcp[1][2], tcpOffset.y],
      [R_tcp[2][0], R_tcp[2][1], R_tcp[2][2], tcpOffset.z],
      [0, 0, 0, 1],
    ];

    // Apply TCP offset: T_final = T_flange * T_tcp
    T06 = multiplyMatrices(T06, T_tcp);
  }

  // Extract position (last column, first 3 rows)
  const position = {
    x: T06[0][3],
    y: T06[1][3],
    z: T06[2][3],
  };

  // Extract rotation matrix (top-left 3x3)
  const R = [
    [T06[0][0], T06[0][1], T06[0][2]],
    [T06[1][0], T06[1][1], T06[1][2]],
    [T06[2][0], T06[2][1], T06[2][2]],
  ];

  // Convert rotation matrix to axis-angle (Rodrigues' rotation formula)
  const rotation = rotationMatrixToAxisAngle(R);

  return {
    position,
    rotation,
    transformMatrix: T06,
  };
}

/**
 * Convert rotation matrix to axis-angle representation (rx, ry, rz)
 * This matches the UR robot representation: rotation vector (axis * angle)
 * @param {Array<Array<number>>} R - 3x3 rotation matrix
 * @returns {Object} Axis-angle representation { rx, ry, rz } in radians
 */
function rotationMatrixToAxisAngle(R) {
  // Calculate rotation angle
  const trace = R[0][0] + R[1][1] + R[2][2];
  const angle = Math.acos((trace - 1) / 2);

  // Handle special cases
  if (Math.abs(angle) < 1e-10) {
    // No rotation
    return { rx: 0, ry: 0, rz: 0 };
  }

  if (Math.abs(angle - Math.PI) < 1e-10) {
    // 180 degree rotation - need to find axis differently
    let axis;
    if (R[0][0] >= R[1][1] && R[0][0] >= R[2][2]) {
      axis = [
        Math.sqrt((R[0][0] + 1) / 2),
        R[0][1] / (2 * Math.sqrt((R[0][0] + 1) / 2)),
        R[0][2] / (2 * Math.sqrt((R[0][0] + 1) / 2)),
      ];
    } else if (R[1][1] >= R[0][0] && R[1][1] >= R[2][2]) {
      axis = [
        R[1][0] / (2 * Math.sqrt((R[1][1] + 1) / 2)),
        Math.sqrt((R[1][1] + 1) / 2),
        R[1][2] / (2 * Math.sqrt((R[1][1] + 1) / 2)),
      ];
    } else {
      axis = [
        R[2][0] / (2 * Math.sqrt((R[2][2] + 1) / 2)),
        R[2][1] / (2 * Math.sqrt((R[2][2] + 1) / 2)),
        Math.sqrt((R[2][2] + 1) / 2),
      ];
    }
    return {
      rx: axis[0] * angle,
      ry: axis[1] * angle,
      rz: axis[2] * angle,
    };
  }

  // General case
  const k = 1 / (2 * Math.sin(angle));
  const axis = [
    k * (R[2][1] - R[1][2]),
    k * (R[0][2] - R[2][0]),
    k * (R[1][0] - R[0][1]),
  ];

  return {
    rx: axis[0] * angle,
    ry: axis[1] * angle,
    rz: axis[2] * angle,
  };
}

/**
 * Convert axis-angle representation to rotation matrix
 * @param {number} rx - Rotation about x-axis in radians
 * @param {number} ry - Rotation about y-axis in radians
 * @param {number} rz - Rotation about z-axis in radians
 * @returns {Array<Array<number>>} 3x3 rotation matrix
 */
export function axisAngleToRotationMatrix(rx, ry, rz) {
  const angle = Math.sqrt(rx * rx + ry * ry + rz * rz);

  if (angle < 1e-10) {
    // No rotation - return identity
    return [
      [1, 0, 0],
      [0, 1, 0],
      [0, 0, 1],
    ];
  }

  // Normalize axis
  const kx = rx / angle;
  const ky = ry / angle;
  const kz = rz / angle;

  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const v = 1 - c;

  // Rodrigues' rotation formula
  return [
    [kx * kx * v + c, kx * ky * v - kz * s, kx * kz * v + ky * s],
    [kx * ky * v + kz * s, ky * ky * v + c, ky * kz * v - kx * s],
    [kx * kz * v - ky * s, ky * kz * v + kx * s, kz * kz * v + c],
  ];
}
